import { useEffect, useRef, useState } from 'react';

import NewLinkBuilerDataSelection from './DataSelection';
import { CurrencyValue, CurrencySelectionType, DateSelectionType, DateValue, TradedFundsPlaceholderName, TIndexLookUpApproach, TradedFundsIdentification } from '../../../types/newLinkBuilder';
import FundIdentification from './FundIdentification';
import { Button, Form, FormInstance } from 'antd';
import DateAndCurrencyForm from './DateAndCurrencyForm';
import { DialogMessageType } from '@/types/commands';
import FormulaBuilderForm from "../FormulaBuilderForm";
import { useStyles } from '@/components/style';
import { validateForm } from '@/utils/validation';
import { buildFormula, FormulaType } from '@/utils/formulaBuilder';
import { TradedFundFilters, FUND_TYPE_OPTIONS, TFundType, TRecommendationType } from '../../../types/newLinkBuilder';
import useDebounce from '../../../hooks/useDebounce';
import { useFetchRecommendedTradedFunds } from '@/hooks/useFetchRecommendedTradedFunds';

export const fundTypeOptions = {
    [TIndexLookUpApproach.RecommendationTool]: FUND_TYPE_OPTIONS,
    [TIndexLookUpApproach.SearchByFundName]: FUND_TYPE_OPTIONS,
}

const NewLinkBuilder = () => {

    const [form] = Form.useForm();
    const styles = useStyles();


    const [tradedFundsIdentification, setTradedFundsIdentification] = useState<TradedFundsIdentification>({
        selected: TIndexLookUpApproach.RecommendationTool,
        index: '',
        exchange: '',
        ticker: '',
    });
    const [searchValue, setSearchValue] = useState<string>("");
    const [tradedFundFilters, setTradedFundFilters] = useState<TradedFundFilters>({
        fundType: TFundType.Index,
        region: "global",
        maxResults: 5,
    });
    const [recommendationType, setRecommendationType] = useState<TRecommendationType>(TRecommendationType.Niche);

    const [isVolumeAndTrendingCurrencySelected, setIsVolumeAndTrendingCurrencySelected] = useState(false);
    const [recommendedTradedFunds, setRecommendedTradedFunds] = useState<any>();

    const [currency, setCurrency] = useState<CurrencyValue>({
        selected: CurrencySelectionType.Value,
        currency: "Trading_Currency",
        currencyCellReference: "",
    }); 

    const [date, setDate] = useState<DateValue>({
        selected: DateSelectionType.Date,
        date: "",
        dateCellReference: "",
    });

    const [formula, setFormula] = useState<string>("");
    const [formulaLocation, setFormulaLocation] = useState<string>("");
    const [scrollTarget, setScrollTarget] = useState<'fundIdentification' | 'dateAndCurrency' | 'formula' | null>(null);

    const fundIdentificationRef = useRef<HTMLDivElement>(null);
    const dateAndCurrencyRef = useRef<HTMLDivElement>(null);
    const formulaBuilderRef = useRef<HTMLDivElement>(null);
    const [isRestoring, setIsRestoring] = useState(true);

    const handleVolumeOrCurrencySelection = (isSelected: boolean) => {
        setIsVolumeAndTrendingCurrencySelected(isSelected);
    };


    const handlePlaceholder = (name: TradedFundsPlaceholderName) => {
        const value = {
            placeholderName: name,
            data: {
                selectedDataPoint,
                date,
                currency,
                index: tradedFundsIdentification.index,
                formulaLocation,
                indexLookUpApproach: tradedFundsIdentification.selected,
                searchValue,
                tradedFundFilters,
                recommendationType,
                recommendedTradedFunds,
                exchange: tradedFundsIdentification.exchange,
                ticker: tradedFundsIdentification.ticker,
            }
        }

        localStorage.setItem("placeholder", JSON.stringify(value));
        localStorage.setItem("redirectTo", "traded-funds");
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.OPEN_PLACEHOLDER,
        }));
    }
    const [selectedDataPoint, setSelectedDataPoint] = useState<string | null>('Closing_Share_Price');
    const debouncedSearchValue = useDebounce(searchValue, 500);

    const { data: searchResults, loading: searchLoading } = useFetchRecommendedTradedFunds(
        { searchValue: debouncedSearchValue, indexLookUpApproach: tradedFundsIdentification.selected, tradedFundFilters, recommendationType, enabled: !isRestoring && tradedFundsIdentification.selected !== TIndexLookUpApproach.CellReference }
    );

    useEffect(() => {
        if (isRestoring || tradedFundsIdentification.selected === TIndexLookUpApproach.CellReference) {
            return;
        }

        setRecommendedTradedFunds(searchResults);

        const firstFund = (searchResults && searchResults.length > 0) ? searchResults[0] : null;
        if (firstFund) {
            setTradedFundsIdentification(prev => ({ ...prev, index: firstFund.symbol }));
        } else {
            setTradedFundsIdentification(prev => ({ ...prev, index: '' }));
        }
    }, [searchResults, isRestoring, tradedFundsIdentification.selected]);

    const handleOk = (form: FormInstance) => {
        const indexError = validateForm(tradedFundsIdentification, form, "index");
        const dateError = validateForm(date, form, "asOfDate");
        const currencyError = isVolumeAndTrendingCurrencySelected ? false : validateForm(currency, form, "currencyType");
        const formulaLocationError = validateForm(formulaLocation, form, "formulaLocation");

        const hasError = indexError || dateError || currencyError || formulaLocationError;

        if (!hasError) {
            Office.context.ui.messageParent(JSON.stringify({
                action: DialogMessageType.ADD_LINK_TO_CELL,
                data: {
                    formula: formula,
                    address: formulaLocation,
                },
            }));
        }
    }

    const handleCancel = () => {
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.CLOSE,
        }));
    }

    useEffect(() => {
        const buildFormulaAsync = async () => {
            const formula = await buildFormula(
                FormulaType.TradedFunds,
                {
                    selectedDataPoint,
                    indexIdentification: tradedFundsIdentification,
                    date,
                    currency
                }
            );
            setFormula(formula);
        };
        buildFormulaAsync();
    }, [selectedDataPoint, tradedFundsIdentification, date, currency, formulaLocation]);

    useEffect(() => {
        if (scrollTarget === 'fundIdentification') {
            fundIdentificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        }
        if (scrollTarget === 'dateAndCurrency') {
            dateAndCurrencyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'formula') {
            formulaBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        }
    }, [scrollTarget]);

    useEffect(() => {
        form.setFieldsValue({
            asOfDate: date.selected,
            currencyType: currency.selected
        });
    }, [date.selected, currency.selected]);

    useEffect(() => {
        const placeholder = localStorage.getItem("placeholder");
        const cellAddress = localStorage.getItem("cellAddress");
        const activeCell = localStorage.getItem("activeCell");

        if (activeCell) {
            setFormulaLocation(activeCell);
        }

        if (placeholder) {
            const placeholderData = JSON.parse(placeholder);

            setSelectedDataPoint(placeholderData.data.selectedDataPoint);
            setIsVolumeAndTrendingCurrencySelected(['Traded_Volume_Shares', 'Trading_Currency', 'Trading_Days'].includes(placeholderData.data.selectedDataPoint));
            setTradedFundsIdentification(prev => ({ ...prev, index: placeholderData.data.index }));
            setDate(placeholderData.data.date);
            setCurrency(placeholderData.data.currency);
            setFormulaLocation(placeholderData.data.formulaLocation);
            setTradedFundsIdentification(prev => ({ ...prev, selected: placeholderData.data.indexLookUpApproach as TIndexLookUpApproach }));
            setSearchValue(placeholderData.data.searchValue);
            setTradedFundFilters(placeholderData.data.tradedFundFilters);
            setRecommendationType(placeholderData.data.recommendationType as TRecommendationType);
            setRecommendedTradedFunds(placeholderData.data.recommendedTradedFunds || []);
            setTradedFundsIdentification(prev => ({ ...prev, exchange: placeholderData.data.exchange || '', ticker: placeholderData.data.ticker || '' }));

            switch (placeholderData.placeholderName) {
                case TradedFundsPlaceholderName.DateCellReference:
                    setDate(prev => ({
                        ...prev,
                        selected: DateSelectionType.CellReference,
                        dateCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case TradedFundsPlaceholderName.CurrencyCellReference:
                    setCurrency(prev => ({
                        ...prev,
                        selected: CurrencySelectionType.CellReference,
                        currencyCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case TradedFundsPlaceholderName.FormulaLocationCellReference:
                    setFormulaLocation(cellAddress);
                    setScrollTarget('formula');
                    break;
                case TradedFundsPlaceholderName.FundIdentificationExchangeCellReference:
                    setTradedFundsIdentification(prev => ({ ...prev, selected: TIndexLookUpApproach.CellReference, exchange: cellAddress }));
                    setScrollTarget('fundIdentification');
                    break;
                case TradedFundsPlaceholderName.FundIdentificationTickerCellReference:
                    setTradedFundsIdentification(prev => ({ ...prev, selected: TIndexLookUpApproach.CellReference, ticker: cellAddress }));
                    setScrollTarget('fundIdentification');
                    break;
                default:
                    break;
            }
        } else {
            setIsRestoring(false);
        }

        return () => {
            localStorage.removeItem("placeholder");
            localStorage.removeItem("cellAddress");
        }
    }, []); 

    useEffect(() => {
        if (tradedFundsIdentification.selected === TIndexLookUpApproach.CellReference) {
            setSearchValue("");
            setRecommendedTradedFunds([]);
        }
    }, [tradedFundsIdentification.selected]);

    return (
        <Form 
            initialValues={{
                asOfDate: DateSelectionType.Date,
                currencyType: CurrencySelectionType.Value
            }}
            onValuesChange={(changedValues) => {
                if (changedValues.asOfDate !== undefined) {
                    setDate(prev => ({
                        ...prev,
                        selected: changedValues.asOfDate,
                        ...(changedValues.asOfDate === DateSelectionType.Date
                            ? { dateCellReference: "" }
                            : { date: "" })
                    }));
                }
                if (changedValues.currencyType !== undefined) {
                    setCurrency(prev => ({
                        ...prev,
                        selected: changedValues.currencyType,
                        ...(changedValues.currencyType === CurrencySelectionType.Value
                            ? { currencyCellReference: "", currency: "Trading_Currency" }
                            : { currency: "" })
                    }));
                }
            }}
            form={form}
            className={styles.radio}
        >
            <NewLinkBuilerDataSelection
                selectedDataPoint={selectedDataPoint}
                setSelectedDataPoint={setSelectedDataPoint}
                onVolumeOrCurrencySelected={handleVolumeOrCurrencySelection}
            />

            <div ref={fundIdentificationRef}>
                <FundIdentification
                    tradedFundsIdentification={tradedFundsIdentification}
                    setTradedFundsIdentification={setTradedFundsIdentification}
                    tradedFundFilters={tradedFundFilters}
                    setTradedFundFilters={setTradedFundFilters}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    searchLoading={searchLoading}
                    recommendedTradedFunds={recommendedTradedFunds}
                    setRecommendedTradedFunds={setRecommendedTradedFunds}
                    recommendationType={recommendationType}
                    setRecommendationType={setRecommendationType}
                    isRestoring={isRestoring}
                    setIsRestoring={setIsRestoring}
                    handlePlaceholder={handlePlaceholder}
                />
            </div>

            <div ref={dateAndCurrencyRef}>
                <DateAndCurrencyForm
                    dateValue={date}
                    setDateValue={setDate}
                    currencyValue={currency}
                    setCurrencyValue={setCurrency}
                    handlePlaceholder={handlePlaceholder}
                    isVolumeAndTrendingCurrencySelected={isVolumeAndTrendingCurrencySelected}
                />
            </div>

            <div ref={formulaBuilderRef}>
                <FormulaBuilderForm formula={formula} formulaLocation={formulaLocation} setFormulaLocation={setFormulaLocation} handlePlaceholder={handlePlaceholder} />
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                marginBlock: '20px',
            }}>
                <Button type="primary" onClick={() => handleOk(form)}>Ok</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </div>
        </Form>
    );
};


export default NewLinkBuilder;